import React, { useState, useRef } from "react";
import moment from "moment";
import { MdCalendarToday } from "react-icons/md";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ActionButton from "../component/Common/ActionButton";
const presets = [
  { label: "Today", getRange: () => [moment(), moment()] },
  { label: "Last 1 week", getRange: () => [moment().subtract(6, "days"), moment()] },
  { label: "Last 30 days", getRange: () => [moment().subtract(29, "days"), moment()] },
  {
    label: "Last 2 months",
    getRange: () => [moment().subtract(2, "months").startOf("month"), moment()],
  },
  {
    label: "Last 3 months",
    getRange: () => [moment().subtract(3, "months").startOf("month"), moment()],
  },
  {
    label: "Last 12 months",
    getRange: () => [moment().subtract(12, "months").startOf("month"), moment()],
  },
  { label: "Month to date", getRange: () => [moment().startOf("month"), moment()] },
];

const getDaysMatrix = (momentDate) => {
  const startOfMonth = momentDate.clone().startOf("month");
  const endOfMonth = momentDate.clone().endOf("month");
  const startDate = startOfMonth.clone().startOf("isoWeek");
  const endDate = endOfMonth.clone().endOf("isoWeek");

  const days = [];
  const day = startDate.clone().subtract(1, "day");
  while (days.length < 42 || day.isBefore(endDate, "day")) {
    days.push(day.add(1, "day").clone());
    if (days.length === 42) {
      break;
    }
  }

  const calendar = [];
  for (let i = 0; i < 6; i++) {
    calendar.push(days.slice(i * 7, (i + 1) * 7));
  }
  return calendar;
};

const CalendarMonth = ({
  monthDate,
  onNext,
  onPrev,
  onDayClick,
  startDate,
  endDate,
  hoverDate,
  setHoverDate,
  showPrevNav = true,
  showNextNav = true,
}) => {
  const matrix = getDaysMatrix(monthDate);
  const dayNames = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  const isSelected = (day) => {
    if (startDate && day.isSame(startDate, "day")) {
      return true;
    }
    if (endDate && day.isSame(endDate, "day")) {
      return true;
    }
    return false;
  };

  const isBetween = (day) => {
    if (startDate && endDate) {
      return day.isAfter(startDate, "day") && day.isBefore(endDate, "day");
    }
    if (startDate && !endDate && hoverDate) {
      if (hoverDate.isAfter(startDate, "day")) {
        return day.isAfter(startDate, "day") && day.isBefore(hoverDate, "day");
      } else if (hoverDate.isBefore(startDate, "day")) {
        return day.isAfter(hoverDate, "day") && day.isBefore(startDate, "day");
      }
    }
    return false;
  };

  return (
    <div className="calendar-month">
      <div className="calendar-header">
        <button
          onClick={onPrev}
          className="month-nav"
          style={{ visibility: showPrevNav ? "visible" : "hidden" }}
        >
          <FaChevronLeft size={12} />
        </button>
        <div className="month-name">{monthDate.format("MMMM")}</div>
        <button
          onClick={onNext}
          className="month-nav"
          style={{ visibility: showNextNav ? "visible" : "hidden" }}
        >
          <FaChevronRight size={12} />
        </button>
      </div>
      <div className="calendar-weekdays">
        {dayNames.map((name) => (
          <div key={name} className="weekday">
            {name}
          </div>
        ))}
      </div>
      <div className="calendar-days">
        {matrix.map((row, i) => (
          <div key={i} className="calendar-row">
            {row.map((day, j) => {
              const isCurrentMonth = day.month() === monthDate.month();
              const selected = isSelected(day);
              const between = isBetween(day);
              let classes = `calendar-day ${isCurrentMonth ? "current-month" : "other-month"}`;
              if (selected) {
                classes += " selected";
              }
              if (between) {
                classes += " between";
              }

              return (
                <div
                  key={day.format("YYYYMMDD")}
                  className={classes}
                  onClick={() => onDayClick(day)}
                  onMouseEnter={() => setHoverDate(day)}
                >
                  {day.date()}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const DateRangeFilter = ({ onChange, initialFrom, initialTo, align = "auto" }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [startDate, setStartDate] = useState(initialFrom ? moment(initialFrom) : null);
  const [endDate, setEndDate] = useState(initialTo ? moment(initialTo) : null);

  const prevInitialFrom = useRef(initialFrom);
  const prevInitialTo = useRef(initialTo);

  React.useEffect(() => {
    if (initialFrom !== prevInitialFrom.current) {
      setStartDate(initialFrom ? moment(initialFrom) : null);
      prevInitialFrom.current = initialFrom;
    }
    if (initialTo !== prevInitialTo.current) {
      setEndDate(initialTo ? moment(initialTo) : null);
      prevInitialTo.current = initialTo;
    }
  }, [initialFrom, initialTo]);

  const [dropdownAlign, setDropdownAlign] = useState(align === "right" ? "right" : "left");

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const checkAlignment = () => {
      if (align === "right") {
        setDropdownAlign("right");
        return;
      }
      if (align === "left") {
        setDropdownAlign("left");
        return;
      }
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const popupWidth = window.innerWidth <= 1100 ? 600 : 680;
        if (rect.left + popupWidth > window.innerWidth - 16) {
          setDropdownAlign("right");
        } else {
          setDropdownAlign("left");
        }
      }
    };

    checkAlignment();
    window.addEventListener("resize", checkAlignment);
    return () => {
      window.removeEventListener("resize", checkAlignment);
    };
  }, [isOpen, align]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const [leftMonth, setLeftMonth] = useState(moment().startOf("month"));

  const [hoverDate, setHoverDate] = useState(null);
  const containerRef = useRef(null);
  const presetRef = useRef(null);

  const scrollPresets = (direction) => {
    if (presetRef.current) {
      const scrollAmount = 150;
      presetRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleOpen = () => {
    if (!isOpen) {
      if (startDate) {
        setLeftMonth(startDate.clone().startOf("month"));
      } else {
        setLeftMonth(moment().subtract(1, "month").startOf("month"));
      }
    }
    setIsOpen(!isOpen);
  };

  const applyPreset = (preset) => {
    const [start, end] = preset.getRange();
    setStartDate(start);
    setEndDate(end);
    setLeftMonth(start.clone().startOf("month"));
  };

  const applyDates = () => {
    if (startDate && endDate) {
      onChange &&
        onChange({
          from: startDate.format("YYYY-MM-DD"),
          to: endDate.format("YYYY-MM-DD"),
        });
      setIsOpen(false);
    }
  };

  const handleDayClick = (day) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(day);
      setEndDate(null);
    } else {
      if (day.isBefore(startDate, "day")) {
        setEndDate(startDate);
        setStartDate(day);
      } else {
        setEndDate(day);
      }
    }
  };

  const rightMonth = leftMonth.clone().add(1, "month");

  const getTriggerLabel = () => {
    if (!startDate || !endDate) {
      return "Select Date";
    }
    return `${startDate.format("DD/MM/YYYY")} - ${endDate.format("DD/MM/YYYY")}`;
  };

  return (
    <div className="date-range-container" ref={containerRef}>
      <div className="date-input-trigger custom-date-trigger" onClick={handleOpen}>
        <MdCalendarToday size={14} color="#8a98ac" />
        <span>{getTriggerLabel()}</span>
      </div>

      {isOpen && (
        <>
          <div className="date-picker-overlay" onClick={() => setIsOpen(false)} />
          <div className={`date-picker-popup ${dropdownAlign === "right" ? "align-right" : ""}`}>
            <div className="date-picker-body">
              <div className="preset-panel-wrapper">
                <button className="preset-nav-btn left" onClick={() => scrollPresets("left")}>
                  <FaChevronLeft size={10} />
                </button>
                <div className="preset-panel" ref={presetRef}>
                  {presets.map((p, i) => {
                    const range = p.getRange();
                    const active =
                      startDate &&
                      endDate &&
                      startDate.isSame(range[0], "day") &&
                      endDate.isSame(range[1], "day");
                    return (
                      <div
                        key={i}
                        onClick={() => applyPreset(p)}
                        className={`preset-item ${active ? "active" : ""}`}
                      >
                        {p.label}
                      </div>
                    );
                  })}
                </div>
                <button className="preset-nav-btn right" onClick={() => scrollPresets("right")}>
                  <FaChevronRight size={10} />
                </button>
              </div>
              <div className="calendars-wrapper">
                <div className="calendars-container">
                  <CalendarMonth
                    monthDate={leftMonth}
                    onNext={() => {}}
                    onPrev={() => setLeftMonth(leftMonth.clone().subtract(1, "month"))}
                    showNextNav={false}
                    onDayClick={handleDayClick}
                    startDate={startDate}
                    endDate={endDate}
                    hoverDate={hoverDate}
                    setHoverDate={setHoverDate}
                  />
                  <div className="calendar-divider"></div>
                  <CalendarMonth
                    monthDate={rightMonth}
                    onNext={() => setLeftMonth(leftMonth.clone().add(1, "month"))}
                    onPrev={() => {}}
                    showPrevNav={false} // Match mockup where < is only on the left calendar
                    onDayClick={handleDayClick}
                    startDate={startDate}
                    endDate={endDate}
                    hoverDate={hoverDate}
                    setHoverDate={setHoverDate}
                  />
                </div>
                <div className="date-picker-footer">
                  <div className="selected-dates-display">
                    <div className="date-display-box">
                      {startDate ? startDate.format("DD/MM/YYYY") : "DD/MM/YYYY"}
                    </div>
                    <span className="arrow-sep">→</span>
                    <div className="date-display-box">
                      {endDate ? endDate.format("DD/MM/YYYY") : "DD/MM/YYYY"}
                    </div>
                  </div>
                  <div className="actions">
                    <ActionButton
                      onClick={() => {
                        setStartDate(null);
                        setEndDate(null);
                        onChange && onChange({ from: "", to: "" });
                        setIsOpen(false);
                      }}
                      text="Cancel"
                      type="button"
                      variant="cancel"
                    />
                    <ActionButton text="Apply" type="button" onClick={applyDates} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* </div> */}
        </>
      )}
    </div>
  );
};

export default DateRangeFilter;
